import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useSEO } from '../hooks/useSEO';

const PhilosophyPage: React.FC = () => {
  useSEO({
    title: 'The Aura Philosophy — Time, Revenue & Status | Aura Traveler',
    description:
      'Understand the asymmetric upside behind Aura Traveler. How generative AI, editorial design, and real-time intelligence combine to transform luxury travel agencies.',
    canonical: 'https://www.auratraveler.com/philosophy',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'Philosophy', url: 'https://www.auratraveler.com/philosophy' },
    ],
  });

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      <SiteNav />

      <main>
      {/* Hero */}
      <section className="pt-40 pb-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/5 blur-[120px] rounded-full -mr-64 -mt-64 pointer-events-none" />
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold/80 mb-8 block">
              The Philosophy
            </span>
            <h1 className="font-display text-4xl sm:text-6xl md:text-8xl text-white leading-[0.92] tracking-tighter font-bold mb-10">
              Time. Revenue.{' '}
              <span className="font-serif italic font-medium text-gold">Status.</span>
              <br />
              The Asymmetric Upside.
            </h1>
            <p className="font-serif text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed font-medium">
              Aura Traveler is not merely a booking engine — it is a generative design intelligence
              calibrated for the luxury sector. We believe that the right tool should compound your
              expertise, not merely automate your tasks.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Philosophy */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-t border-white/5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-24 items-start">
          <div>
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-8 block">
              01. Time
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-tight tracking-tighter font-bold mb-8">
              The Planning Problem
            </h2>
            <p className="font-serif text-lg text-white/80 leading-relaxed mb-8 font-medium">
              The average luxury travel advisor spends over 12 hours crafting a single custom
              itinerary from scratch. That time is consumed by research, cross-referencing,
              link-checking, writing, and design. It is time that cannot be billed, cannot be
              scaled, and cannot be recovered.
            </p>
            <p className="font-serif text-lg text-white/80 leading-relaxed font-medium">
              Aura compresses this to under 90 seconds. Not by cutting corners, but by synthesizing
              over 500 variables per destination — accommodations, dining, logistics, aesthetics,
              seasonal conditions — into a single generative pass that produces publication-quality
              output immediately.
            </p>
          </div>
          <div className="glass-panel p-10 md:p-16 border border-white/5">
            <h3 className="font-display text-2xl text-white mb-12 uppercase tracking-[0.3em]">
              The Time You Reclaim
            </h3>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-4 font-sans text-[10px] uppercase tracking-[0.3em] text-white/70">
                    Task
                  </th>
                  <th className="py-4 font-sans text-[10px] uppercase tracking-[0.3em] text-white/70">
                    Manual
                  </th>
                  <th className="py-4 font-sans text-[10px] uppercase tracking-[0.3em] text-gold/80">
                    With Aura
                  </th>
                </tr>
              </thead>
              <tbody className="font-serif text-base">
                {[
                  ['Market Research', '270 min', '0.8s'],
                  ['Route Optimization', '180 min', '0.4s'],
                  ['Content Writing', '240 min', 'Instant'],
                  ['Itinerary Design', '120 min', 'Automatic'],
                ].map(([task, manual, aura]) => (
                  <tr key={task} className="border-b border-white/5 hover:bg-white/5 transition-colors duration-300">
                    <td className="py-5 text-white/80">{task}</td>
                    <td className="py-5 text-white/75">{manual}</td>
                    <td className="py-5 text-gold font-display text-xl tracking-tighter">{aura}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Travel image break */}
      <section className="relative h-[35vh] md:h-[45vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?q=80&w=2000&auto=format&fit=crop&fm=webp"
          alt="Luxury resort pool with palm trees at golden hour"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-transparent to-[#0F0F0F]" />
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="font-serif text-2xl md:text-3xl text-white italic text-center max-w-2xl px-8 drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
            "The right presentation doesn't just inform — it converts."
          </p>
        </div>
      </section>

      {/* Revenue Section */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0F0F0F] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
            className="relative aspect-square max-w-sm mx-auto lg:mx-0"
          >
            <div className="absolute inset-0 border border-white/10 rounded-full animate-spin-slow" />
            <div className="absolute inset-12 border border-gold/20 rounded-full animate-reverse-spin-slow" />
            <div className="absolute inset-0 flex items-center justify-center text-center">
              <div>
                <span className="block text-[64px] font-display text-gold leading-none">16h→1h</span>
                <span className="text-[11px] uppercase tracking-[0.4em] text-white/80 block mt-2">
                  Production Time
                </span>
              </div>
            </div>
          </motion.div>
          <div>
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-8 block">
              02. Revenue
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-tight tracking-tighter font-bold mb-8">
              Asymmetric Growth
            </h2>
            <p className="font-serif text-lg text-white/80 leading-relaxed mb-8 font-medium">
              Travelers spend an average of 16 hours researching a trip (Priceline/Harris research);
              Aura is built to deliver the finished document in under an hour of your time. When
              clients receive a magazine-quality document, they perceive premium value before the
              first conversation about pricing.
            </p>
            <p className="font-serif text-lg text-white/80 leading-relaxed font-medium">
              This perception gap — between what an itinerary looks like and what a client is willing to
              pay — is the single largest untapped revenue lever available to independent luxury
              travel advisors today.
            </p>
          </div>
        </div>
      </section>

      {/* Travel image break */}
      <section className="relative h-[30vh] md:h-[40vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2000&auto=format&fit=crop&fm=webp"
          alt="Luxury hotel suite with panoramic mountain views"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F]/50 via-transparent to-charcoal" />
      </section>

      {/* Status Section */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-16">
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-8 block">
              03. Status
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-tight tracking-tighter font-bold mb-8">
              Elite Positioning
            </h2>
            <p className="font-serif text-lg text-white/80 leading-relaxed font-medium">
              In the luxury market, status is not declared — it is demonstrated. The artifacts you
              produce for your clients communicate your position as clearly as any testimonial or
              award. Magazine-quality layouts and cinematic social reels position your brand among
              the top 1% of travel professionals globally.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {[
              {
                label: 'White-Label Branding',
                body:
                  'Every itinerary carries your agency name, your color palette, your logo. Clients associate the quality of the output with the authority of your brand.',
              },
              {
                label: 'Cinematic Visual Direction',
                body:
                  'AI-directed destination imagery, and editorial typography that would not look out of place in Condé Nast Traveler.',
              },
              {
                label: 'Instant Social Studio',
                body:
                  'Generate 9:16 vertical reels with cinematic motion to showcase destinations on Instagram and TikTok — without a videographer.',
              },
            ].map(({ label, body }) => (
              <div
                key={label}
                className="bg-[#141414] p-10 md:p-16 hover:bg-[#1a1a1a] transition-all duration-500 group"
              >
                <h3 className="font-display text-2xl text-white mb-6 tracking-tight group-hover:text-gold transition-colors duration-300">
                  {label}
                </h3>
                <p className="font-serif text-base text-white/70 leading-relaxed group-hover:text-white/80 transition-colors duration-500">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Deep Dive */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/80 mb-8 block">
            The Methodology
          </span>
          <h2 className="font-display text-3xl md:text-5xl text-white leading-tight tracking-tighter font-bold mb-16 max-w-2xl">
            Why it takes 14 hours to plan a trip — and how we fixed it
          </h2>
          <div className="space-y-12">
            {[
              {
                n: '01',
                title: 'Research Complexity',
                body:
                  'The average traveler visits 38 websites before booking. We synthesize this across 500+ variables in a single pass, producing recommendations that reflect genuine editorial judgment, not averaged crowd data.',
              },
              {
                n: '02',
                title: 'Logistics & Routing',
                body:
                  'Calculating travel times between restaurants, hotels, and airports while ensuring your schedule flows is one of the most underestimated costs of itinerary design. Aura handles this automatically, down to the minute.',
              },
              {
                n: '03',
                title: 'The Vetting Phase',
                body:
                  'Reading hundreds of reviews and cross-referencing social proof is not how great travel is designed — it is how mediocre travel is approximated. Aura\'s curation layer applies aesthetic and quality filters that reflect the standards of the luxury sector specifically.',
              },
              {
                n: '04',
                title: 'Visual Design',
                body:
                  'Most travel tools assume the advisor is also a graphic designer. Aura is not a blank canvas — it is a fully designed system. Every output arrives typeset, laid out, and print-ready, without a single design decision required from the user.',
              },
            ].map(({ n, title, body }) => (
              <div key={n} className="flex gap-8 group">
                <div className="font-display text-3xl text-gold/60 group-hover:text-gold transition-all duration-500 min-w-[3rem]">
                  {n}
                </div>
                <div>
                  <h4 className="font-display text-xl text-white mb-3 uppercase tracking-[0.2em] group-hover:text-gold transition-colors duration-300">
                    {title}
                  </h4>
                  <p className="font-serif text-base text-white/70 leading-relaxed group-hover:text-white/80 transition-colors duration-500">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-4 sm:px-6 md:px-12 text-center bg-gradient-to-b from-[#0A0A0A] to-charcoal border-t border-white/5">
        <h2 className="font-display text-4xl md:text-7xl text-white mb-8 tracking-tighter">
          Ready to{' '}
          <span className="font-serif italic text-gold">architect</span>
          <br />
          extraordinary journeys?
        </h2>
        <p className="font-serif text-lg text-white/70 mb-12 max-w-xl mx-auto">
          Join the agencies and hotels already using Aura to design the unforgettable.
        </p>
        <div className="flex flex-col sm:flex-row gap-6 justify-center">
          <Link
            to="/pricing"
            className="px-12 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full"
          >
            View Membership
          </Link>
          <Link
            to="/for-agencies"
            className="px-12 py-5 border border-white/20 text-white font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:border-gold hover:text-gold transition-all duration-700 rounded-full"
          >
            Agency Solutions
          </Link>
        </div>
      </section>

      </main>

      <SiteFooter />
    </div>
  );
};

export default PhilosophyPage;
